import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// 基础类型定义
export interface TablePagination {
  current: number;
  pageSize: number;
  total: number;
}

export interface TableSorting {
  field: string;
  order: 'asc' | 'desc';
}

export interface TableState<TFilters = any> {
  pagination: TablePagination;
  filters: TFilters;
  sorting: TableSorting;
}

export interface TableQueryConfig<TFilters = any> {
  // 初始状态
  initialState: TableState<TFilters>;
  
  // URL配置
  urlConfig: {
    syncToUrl: boolean;
    urlKeys: (keyof TableState<TFilters>)[];
    urlPrefix?: string;
  };
  
  // 数据获取函数
  fetchData: (state: TableState<TFilters>) => Promise<{
    results: any[];
    count: number;
    [key: string]: any;
  }>;
  
  // 数据转换函数（可选）
  transformData?: (data: any) => any;
  
  // 防抖配置（可选）
  debounceMs?: number;
}

export interface TableQueryResult<TFilters = any> {
  // 状态
  state: TableState<TFilters>;
  
  // 数据
  data: any[];
  loading: boolean;
  error: string | null;
  
  // 操作函数
  actions: {
    updateFilters: (filters: Partial<TFilters>) => void;
    updatePagination: (pagination: Partial<TablePagination>) => void;
    updateSorting: (sorting: Partial<TableSorting>) => void;
    reset: () => void;
    refresh: () => void;
  };
  
  // 分页配置（用于Ant Design Table）
  paginationConfig: {
    current: number;
    pageSize: number;
    total: number;
    showSizeChanger: boolean;
    showQuickJumper: boolean;
    pageSizeOptions: number[];
    onChange: (page: number, pageSize: number) => void;
    onShowSizeChange: (current: number, size: number) => void;
  };
}

/**
 * 可复用的表格查询Hook
 * 实现单一数据源、强制同步、解耦触发的架构
 */
export function useTableQuery<TFilters = any>(
  config: TableQueryConfig<TFilters>
): TableQueryResult<TFilters> {
  const location = useLocation();
  const navigate = useNavigate();
  
  // 1. 统一状态管理（单一数据源）
  const [state, setState] = useState<TableState<TFilters>>(config.initialState);
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // 防重复请求的引用
  const lastRequestRef = useRef<string>('');
  const abortControllerRef = useRef<AbortController | null>(null);
  const fetchDataRef = useRef(config.fetchData);
  const transformDataRef = useRef(config.transformData);

  useEffect(() => {
    fetchDataRef.current = config.fetchData;
  }, [config.fetchData]);

  useEffect(() => {
    transformDataRef.current = config.transformData;
  }, [config.transformData]);
  
  // 2. URL状态同步
  const urlState = useUrlStateSync(state, config.urlConfig, location, navigate, setState);
  
  // 3. 数据获取函数
  const fetchData = useCallback(async (targetState: TableState<TFilters>) => {
    // 取消之前的请求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // 创建新的AbortController
    abortControllerRef.current = new AbortController();
    
    // 生成请求标识
    const requestKey = JSON.stringify(targetState);
    if (lastRequestRef.current === requestKey) {
      console.log('🔄 跳过重复请求:', requestKey);
      return;
    }
    
    lastRequestRef.current = requestKey;
    
    try {
      setLoading(true);
      setError(null);
      
      console.log('🚀 开始获取数据:', targetState);
      
      const response = await fetchDataRef.current(targetState);
      
      // 数据转换
      const transformedData = transformDataRef.current ? transformDataRef.current(response.results) : response.results;
      
      setData(transformedData);
      
      // 更新总数
      setState(prev => ({
        ...(prev.pagination.total === response.count ? prev : {
          ...prev,
          pagination: {
            ...prev.pagination,
            total: response.count
          }
        })
      }));
      
      console.log('✅ 数据获取成功:', {
        results: transformedData.length,
        count: response.count,
        total: response.count,
        ...targetState
      });
      
      console.log('📊 分页状态更新:', {
        current: targetState.pagination.current,
        pageSize: targetState.pagination.pageSize,
        total: response.count
      });
      
    } catch (err: any) {
      if (err.name === 'AbortError') {
        console.log('🔄 请求被取消');
        return;
      }
      
      console.error('❌ 数据获取失败:', err);
      setError(err.message || '数据获取失败');
    } finally {
      setLoading(false);
    }
  }, []);
  
  // 4. 状态变化监听器（强制同步）
  useEffect(() => {
    if (urlState.isReady && !urlState.isUpdating) {
      console.log('🔄 URL状态同步完成，自动获取数据');
      fetchData(state);
    }
  }, [urlState.isReady, urlState.isUpdating, state, fetchData]);

  // 5. 操作函数（解耦触发）
  const actions = {
    updateFilters: useCallback((filters: Partial<TFilters>) => {
      console.log('🔍 更新筛选条件:', filters);
      setState(prev => ({
        ...prev,
        filters: { ...prev.filters, ...filters },
        pagination: { ...prev.pagination, current: 1 } // 筛选变化时重置到第1页
      }));
    }, []),
    
    updatePagination: useCallback((pagination: Partial<TablePagination>) => {
      console.log('📄 更新分页:', pagination);
      console.log('📄 更新前状态:', state.pagination);
      
      setState(prev => {
        const newState = {
          ...prev,
          pagination: { ...prev.pagination, ...pagination }
        };
        console.log('📄 更新后状态:', newState.pagination);
        
        // ✅ 只更新状态，让 useEffect 监听器自动触发数据获取
        return newState;
      });
    }, []),
    
    updateSorting: useCallback((sorting: Partial<TableSorting>) => {
      console.log('🔄 更新排序:', sorting);
      setState(prev => ({
        ...prev,
        sorting: { ...prev.sorting, ...sorting },
        pagination: { ...prev.pagination, current: 1 } // 排序变化时重置到第1页
      }));
    }, []),
    
    reset: useCallback(() => {
      console.log('🔄 重置所有状态');
      setState(config.initialState);
    }, [config.initialState]),
    
    refresh: useCallback(() => {
      console.log('🔄 刷新数据');
      // 强制刷新：即使 state 未变化，也需要重新拉取（例如点击“搜索/刷新”按钮）
      // 否则会被 lastRequestRef 的“重复请求跳过”逻辑拦截。
      lastRequestRef.current = '';
      fetchData(state);
    }, [fetchData, state])
  };
  
  // 6. 分页配置（用于Ant Design Table）
  const paginationConfig = {
    current: state.pagination.current,
    pageSize: state.pagination.pageSize,
    total: state.pagination.total,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total: number, range: [number, number]) => 
      `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
    pageSizeOptions: [10, 20, 50, 100],
    onChange: (page: number, pageSize: number) => {
      actions.updatePagination({ current: page, pageSize });
    },
    onShowSizeChange: (current: number, size: number) => {
      actions.updatePagination({ current: 1, pageSize: size });
    }
  };
  
  // 7. 清理函数
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);
  
  // 监听路由变化，自动保存状态
  useEffect(() => {
    const handleRouteChange = () => {
      const urlParams = new URLSearchParams(location.search);
      const tabId = urlParams.get('tabId');
      
      if (tabId && state) {
        console.log('🔄 useTableQuery - 路由变化，自动保存状态:', tabId);
        
        // 保存当前状态到localStorage
        const urlStateData = {
          pagination: state.pagination,
          filters: state.filters,
          sorting: state.sorting
        };
        
        localStorage.setItem(`tab_url_state_${tabId}`, JSON.stringify(urlStateData));
        console.log('💾 useTableQuery - 保存状态到localStorage:', tabId, urlStateData);
      }
    };
    
    // 监听popstate事件（浏览器前进/后退）
    window.addEventListener('popstate', handleRouteChange);
    
    // 监听hashchange事件（URL hash变化）
    window.addEventListener('hashchange', handleRouteChange);
    
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      window.removeEventListener('hashchange', handleRouteChange);
    };
  }, [location.search, state]);
  
  return {
    state,
    data,
    loading,
    error,
    actions,
    paginationConfig
  };
}

/**
 * URL状态同步Hook（支持tabId隔离）
 */
function useUrlStateSync<TFilters>(
  state: TableState<TFilters>,
  urlConfig: TableQueryConfig<TFilters>['urlConfig'],
  location: any,
  navigate: any,
  setState: React.Dispatch<React.SetStateAction<TableState<TFilters>>>
) {
  const [isReady, setIsReady] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const isInitialized = useRef(false);
  
  // 获取当前tabId
  const getCurrentTabId = useCallback(() => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get('tabId');
  }, [location.search]);
  
  // 从URL初始化状态（支持tabId隔离）
  useEffect(() => {
    if (!urlConfig.syncToUrl || isInitialized.current) return;
    
    // 添加延迟，确保URL变化完成后再解析
    const timer = setTimeout(() => {
      try {
        const urlParams = new URLSearchParams(location.search);
        const tabId = urlParams.get('tabId');
        const urlState: Partial<TableState<TFilters>> = {};
        
        console.log('🔄 URL状态同步 - 开始解析URL:', {
          location: location.href,
          search: location.search,
          tabId,
          urlParams: Array.from(urlParams.entries())
        });
        
        if (tabId) {
          console.log('🔄 URL状态同步 - 检测到tabId:', tabId);
          
          // 解析带tabId前缀的参数
          urlConfig.urlKeys.forEach(key => {
            if (key === 'pagination') {
              const page = urlParams.get(`${tabId}_page`);
              const pageSize = urlParams.get(`${tabId}_pageSize`);
              if (page || pageSize) {
                urlState.pagination = {
                  current: page ? parseInt(page) : 1,
                  pageSize: pageSize ? parseInt(pageSize) : 10,
                  total: 0
                };
              }
            } else if (key === 'filters') {
              const filters: any = {};
              urlParams.forEach((value, key) => {
                if (key.startsWith(`${tabId}_filter_`)) {
                  const filterKey = key.replace(`${tabId}_filter_`, '');
                  if (value && value !== '') {
                    filters[filterKey] = value;
                  }
                }
              });
              if (Object.keys(filters).length > 0) {
                urlState.filters = filters;
              }
            } else if (key === 'sorting') {
              const sortField = urlParams.get(`${tabId}_sortField`);
              const sortOrder = urlParams.get(`${tabId}_sortOrder`);
              if (sortField || sortOrder) {
                urlState.sorting = {
                  field: sortField || 'created_at',
                  order: (sortOrder as 'asc' | 'desc') || 'desc'
                };
              }
            }
          });
          
          console.log('🔄 URL状态同步 - 从tabId参数解析的状态:', urlState);
          
          // 如果URL中没有状态参数，尝试从localStorage恢复状态
          if (Object.keys(urlState).length === 0) {
            console.log('🔄 URL状态同步 - URL中没有状态参数，尝试从localStorage恢复');
            const savedUrlState = localStorage.getItem(`tab_url_state_${tabId}`);
            if (savedUrlState) {
              try {
                const savedState = JSON.parse(savedUrlState);
                console.log('🔄 URL状态同步 - 从localStorage恢复的状态:', savedState);
                
                // 将localStorage中的状态转换为URL参数格式
                if (savedState.pagination) {
                  urlState.pagination = savedState.pagination;
                }
                if (savedState.filters) {
                  urlState.filters = savedState.filters;
                }
                if (savedState.sorting) {
                  urlState.sorting = savedState.sorting;
                }
                
                console.log('🔄 URL状态同步 - 转换后的状态:', urlState);
              } catch (error) {
                console.error('❌ 解析localStorage状态失败:', error);
              }
            }
          }
          
          // 如果没有找到状态参数，尝试从非tabId前缀的参数中恢复（兼容性）
          if (Object.keys(urlState).length === 0) {
            console.log('🔄 URL状态同步 - 尝试从非tabId前缀参数恢复状态');
            
            urlConfig.urlKeys.forEach(key => {
              if (key === 'pagination') {
                const page = urlParams.get('page');
                const pageSize = urlParams.get('pageSize');
                if (page || pageSize) {
                  urlState.pagination = {
                    current: page ? parseInt(page) : 1,
                    pageSize: pageSize ? parseInt(pageSize) : 10,
                    total: 0
                  };
                }
              } else if (key === 'filters') {
                const filters: any = {};
                urlParams.forEach((value, key) => {
                  if (key.startsWith('filter_')) {
                    const filterKey = key.replace('filter_', '');
                    if (value && value !== '') {
                      filters[filterKey] = value;
                    }
                  }
                });
                if (Object.keys(filters).length > 0) {
                  urlState.filters = filters;
                }
              } else if (key === 'sorting') {
                const sortField = urlParams.get('sortField');
                const sortOrder = urlParams.get('sortOrder');
                if (sortField || sortOrder) {
                  urlState.sorting = {
                    field: sortField || 'created_at',
                    order: (sortOrder as 'asc' | 'desc') || 'desc'
                  };
                }
              }
            });
            
            console.log('🔄 URL状态同步 - 从非tabId前缀参数恢复的状态:', urlState);
          }
        } else {
          // 兼容没有tabId的情况（向后兼容）
          console.log('🔄 URL状态同步 - 未检测到tabId，使用兼容模式');
          
          urlConfig.urlKeys.forEach(key => {
            if (key === 'pagination') {
              const page = urlParams.get('page');
              const pageSize = urlParams.get('pageSize');
              if (page || pageSize) {
                urlState.pagination = {
                  current: page ? parseInt(page) : 1,
                  pageSize: pageSize ? parseInt(pageSize) : 10,
                  total: 0
                };
              }
            } else if (key === 'filters') {
              const filters: any = {};
              urlParams.forEach((value, key) => {
                if (key.startsWith('filter_')) {
                  const filterKey = key.replace('filter_', '');
                  if (value && value !== '') {
                    filters[filterKey] = value;
                  }
                }
              });
              if (Object.keys(filters).length > 0) {
                urlState.filters = filters;
              }
            } else if (key === 'sorting') {
              const sortField = urlParams.get('sortField');
              const sortOrder = urlParams.get('sortOrder');
              if (sortField || sortOrder) {
                urlState.sorting = {
                  field: sortField || 'created_at',
                  order: (sortOrder as 'asc' | 'desc') || 'desc'
                };
              }
            }
          });
          
          console.log('🔄 URL状态同步 - 兼容模式解析的状态:', urlState);
        }
        
        // 更新状态
        if (Object.keys(urlState).length > 0) {
          console.log('🔄 URL状态同步 - 从URL解析的状态:', urlState);
          setState(prev => {
            const newState = { ...prev, ...urlState };
            console.log('🔄 URL状态同步 - 更新后的状态:', newState);
            return newState;
          });
        } else {
          console.log('ℹ️ URL状态同步 - 没有找到任何状态参数');
        }
        
        isInitialized.current = true;
        setIsReady(true);
        
      } catch (error) {
        console.error('URL状态解析失败:', error);
        setIsReady(true);
      }
    }, 50); // 50ms延迟，确保URL变化完成
    
    return () => clearTimeout(timer);
  }, [location.search, urlConfig, setState]);
  
  // 同步状态到URL（支持tabId隔离）
  useEffect(() => {
    if (!urlConfig.syncToUrl || !isInitialized.current) return;
    
    const debounceTimer = setTimeout(() => {
      try {
        const urlParams = new URLSearchParams(location.search);
        const tabId = urlParams.get('tabId');
        
        if (tabId) {
          console.log('🔄 URL状态同步 - 同步到带tabId前缀的URL参数');
          
          // 设置带tabId前缀的参数
          urlConfig.urlKeys.forEach(key => {
            if (key === 'pagination') {
              if (state.pagination.current !== 1) {
                urlParams.set(`${tabId}_page`, state.pagination.current.toString());
              } else {
                urlParams.delete(`${tabId}_page`);
              }
              if (state.pagination.pageSize !== 10) {
                urlParams.set(`${tabId}_pageSize`, state.pagination.pageSize.toString());
              } else {
                urlParams.delete(`${tabId}_pageSize`);
              }
            } else if (key === 'filters') {
              Object.entries(state.filters as Record<string, any>).forEach(([filterKey, value]) => {
                // 修改：让false值也能添加到URL，确保"常规入库"等状态能正确保持
                if (value !== undefined && value !== null && value !== '') {
                  // 包括false、0等"假值"，但不包括undefined、null和空字符串
                  urlParams.set(`${tabId}_filter_${filterKey}`, value.toString());
                } else {
                  // 只有undefined、null和空字符串才删除参数
                  urlParams.delete(`${tabId}_filter_${filterKey}`);
                }
              });
            } else if (key === 'sorting') {
              if (state.sorting.field !== 'created_at') {
                urlParams.set(`${tabId}_sortField`, state.sorting.field);
              } else {
                urlParams.delete(`${tabId}_sortField`);
              }
              if (state.sorting.order !== 'desc') {
                urlParams.set(`${tabId}_sortOrder`, state.sorting.order);
              } else {
                urlParams.delete(`${tabId}_sortOrder`);
              }
            }
          });
          
          // 保存状态到localStorage，用于标签页切换时恢复
          const urlState = {
            pagination: state.pagination,
            filters: state.filters,
            sorting: state.sorting
          };
          localStorage.setItem(`tab_url_state_${tabId}`, JSON.stringify(urlState));
          console.log('💾 保存URL状态到localStorage:', tabId, urlState);
          
        } else {
          // 兼容没有tabId的情况（向后兼容）
          console.log('🔄 URL状态同步 - 同步到不带前缀的URL参数');
          
          urlConfig.urlKeys.forEach(key => {
            if (key === 'pagination') {
              if (state.pagination.current !== 1) {
                urlParams.set('page', state.pagination.current.toString());
              } else {
                urlParams.delete('page');
              }
              if (state.pagination.pageSize !== 10) {
                urlParams.set('pageSize', state.pagination.pageSize.toString());
              } else {
                urlParams.delete('pageSize');
              }
            } else if (key === 'filters') {
              Object.entries(state.filters as Record<string, any>).forEach(([filterKey, value]) => {
                // 修改：让false值也能添加到URL，确保"常规入库"等状态能正确保持
                if (value !== undefined && value !== null && value !== '') {
                  // 包括false、0等"假值"，但不包括undefined、null和空字符串
                  urlParams.set(`filter_${filterKey}`, value.toString());
                } else {
                  // 只有undefined、null和空字符串才删除参数
                  urlParams.delete(`filter_${filterKey}`);
                }
              });
            } else if (key === 'sorting') {
              if (state.sorting.field !== 'created_at') {
                urlParams.set('sortField', state.sorting.field);
              } else {
                urlParams.delete('sortField');
              }
              if (state.sorting.order !== 'desc') {
                urlParams.set('sortOrder', state.sorting.order);
              } else {
                urlParams.delete('sortOrder');
              }
            }
          });
        }
        
        // 更新URL
        const newSearch = urlParams.toString();
        const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
        
        if (newUrl !== location.href) {
          console.log('🔄 URL状态同步 - 更新URL:', newUrl);
          window.history.replaceState(null, '', newUrl);
        }
        
      } catch (error) {
        console.error('URL状态同步失败:', error);
      }
    }, 500); // 500ms防抖
    
    return () => clearTimeout(debounceTimer);
  }, [state.pagination.current, state.pagination.pageSize, state.filters, state.sorting.field, state.sorting.order, urlConfig, location.pathname, isInitialized]);
  
  return { isReady, isUpdating };
}
